import type {
  ExtractTaskInitialQuestionResponse,
  SocketUpdateFirstInitialQuestion,
  TaskInitialQuestionResponse,
} from '@frontend/api-types';
import { EArray, P, filterNullish, identity, match } from '@frontend/duck-tape';
import type { InitialQuestionWithAnswerV2 } from '../types';

export const generateEmptyInitialQuestionWithAnswer = (
  iq:
    | Pick<ExtractTaskInitialQuestionResponse<'multi_choice'>, 'question' | 'type'>
    | Pick<ExtractTaskInitialQuestionResponse<'secure_note'>, 'secureNoteTitle' | 'type'>
    | Pick<ExtractTaskInitialQuestionResponse<'text'>, 'question' | 'type'>,
): MaybeUndefined<InitialQuestionWithAnswerV2> =>
  match(iq)
    .with({ type: 'text' }, ({ question, type }) => ({
      answer: '',
      question,
      questionId: undefined,
      type,
    }))
    .with({ type: 'secure_note' }, ({ secureNoteTitle = '', type }) => ({
      questionId: undefined,
      secureNoteId: undefined,
      secureNoteTitle,
      type,
    }))
    .with({ type: 'multi_choice' }, ({ question = '', type }) => ({
      answer: [],
      choices: [],
      question,
      questionId: undefined,
      type,
    }))
    .otherwise(() => undefined);

export const convertSocketInitialQuestionToInitialQuestionWithAnswer = (
  iq: SocketUpdateFirstInitialQuestion['content']['first_question'],
): MaybeUndefined<InitialQuestionWithAnswerV2> => {
  return match<typeof iq, MaybeUndefined<InitialQuestionWithAnswerV2>>(iq)
    .with({ type: 'text' }, (iq) => ({ ...iq, answer: '' }))
    .with({ type: 'secure_note' }, (iq) => ({
      ...iq,
      title: iq.secureNoteTitle,
    }))
    .with({ type: 'multi_choice' }, (iq) => ({
      ...iq,
      answer: [],
    }))
    .otherwise(() => undefined);
};

export const convertAIQuestionnaireToInitialQuestionsWithAnswers = (
  responses: TaskInitialQuestionResponse[],
): InitialQuestionWithAnswerV2[] => {
  return responses
    ? filterNullish(
        responses.map((q) =>
          match(q)
            .with({ type: 'multi_choice' }, (iq) => {
              const answers = EArray.reduce(iq.answer, [] as { id: string; value: string }[], (acc, answer) => {
                if (iq.choices.includes(answer)) {
                  return [
                    ...acc,
                    {
                      id: answer,
                      value: answer,
                    },
                  ];
                } else {
                  return [
                    ...acc,
                    {
                      id: 'Other',
                      value: answer,
                    },
                  ];
                }
              });

              return {
                ...iq,
                answer: answers,
              };
            })
            .with({ type: 'text' }, identity)
            .with({ type: 'secure_note' }, (iq) => ({ ...iq, title: iq.secureNoteTitle }))
            .otherwise(() => undefined),
        ),
      )
    : [];
};

export const getIQTitle = (iq: InitialQuestionWithAnswerV2 | TaskInitialQuestionResponse): MaybeUndefined<string> => {
  return match(iq)
    .with({ type: 'secure_note' }, (iq) => iq.secureNoteTitle)
    .with({ type: P.union('multi_choice', 'text') }, (iq) => iq.question)
    .otherwise(() => '');
};
